import type { Fetcher } from "../fetcher";
import type {
  VehicleFilters,
  CreateVehicleDto,
  UpdateVehicleDto,
  AssignDriverDto,
} from "./types";
import type { Vehicle, DriverAssignment } from "../entities/vehicle";
import type { ApiSingleResponse, ApiCollectionResponse } from "../types/api";

export function createVehiclesRepository(fetcher: Fetcher) {
  return {
    async list(filters: VehicleFilters = {}): Promise<{
      items: Vehicle[];
      pagination: { total: number };
    }> {
      const params = new URLSearchParams();
      if (filters.page) params.append("page", String(filters.page));
      if (filters.limit) params.append("limit", String(filters.limit));
      if (filters.status) params.append("status", filters.status);
      if (filters.vehicleNumber) params.append("vehicleNumber", filters.vehicleNumber);
      if (filters.transportTypeId) params.append("transportTypeId", filters.transportTypeId);
      if (filters.loadingMethodId) params.append("loadingMethodId", filters.loadingMethodId);
      if (filters.carrierId) params.append("carrierId", filters.carrierId);
      if (filters.search) params.append("search", filters.search);

      const query = params.toString();
      const url = query ? `/fleet/vehicles?${query}` : "/fleet/vehicles";

      const response = await fetcher<ApiCollectionResponse<Vehicle>>(url);
      return {
        items: response.data?.items ?? [],
        pagination: { total: response.data?.pagination?.total ?? 0 },
      };
    },

    async get(id: string): Promise<Vehicle> {
      const response = await fetcher<ApiSingleResponse<Vehicle>>(`/fleet/vehicles/${id}`);
      return response.data;
    },

    async create(payload: CreateVehicleDto): Promise<Vehicle> {
      const response = await fetcher<ApiSingleResponse<Vehicle>>("/fleet/vehicles", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      return response.data;
    },

    async update(id: string, payload: UpdateVehicleDto): Promise<Vehicle> {
      const response = await fetcher<ApiSingleResponse<Vehicle>>(`/fleet/vehicles/${id}`, {
        method: "PATCH",
        body: JSON.stringify(payload),
      });
      return response.data;
    },

    async delete(id: string): Promise<{ id: string }> {
      const response = await fetcher<ApiSingleResponse<{ id: string }>>(
        `/fleet/vehicles/${id}`,
        { method: "DELETE" },
      );
      return response.data ?? { id };
    },

    async listAssignments(id: string): Promise<DriverAssignment[]> {
      const response = await fetcher<ApiSingleResponse<DriverAssignment[]>>(
        `/fleet/vehicles/${id}/drivers`,
      );
      return response.data ?? [];
    },

    async assignDriver(id: string, payload: AssignDriverDto): Promise<DriverAssignment> {
      const response = await fetcher<ApiSingleResponse<DriverAssignment>>(
        `/fleet/vehicles/${id}/drivers`,
        { method: "POST", body: JSON.stringify(payload) },
      );
      return response.data;
    },

    async unassignDriver(vehicleId: string, assignmentId: string): Promise<void> {
      await fetcher(`/fleet/vehicles/${vehicleId}/drivers/${assignmentId}`, {
        method: "DELETE",
      });
    },
  };
}
